/**
 * <product-bundle-picker>: renders the components of a Craft My Bundle as clickable options,
 * validates the selection against the registry and adds the configured quantities to the cart.
 * Business rules live in bundle-contract.js; this file only handles the DOM and the cart request.
 */
if (!customElements.get('product-bundle-picker')) {
  customElements.define(
    'product-bundle-picker',
    class ProductBundlePicker extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';

        this.contract = window.BundleContract;
        this.status = this.querySelector('[data-status]');
        this.addButton = this.querySelector('[data-add-button]');
        this.stepsRoot = this.querySelector('[data-steps]');
        this.hint = this.querySelector('[data-hint]');
        this.priceEl = this.querySelector('[data-bundle-price]');

        try {
          this.config = JSON.parse(this.querySelector('[data-bundle-config]')?.textContent || '{}');
        } catch {
          this.config = {};
        }
        this.steps = this.contract.normalizeSteps(this.config);
        if (!this.contract.isValidBundle(this.config, this.steps)) return this.fail();

        this.selections = this.steps.map(() => new Map()); // itemIndex -> variant ids, one per unit (complete only when length === item.qty)
        this.products = new Map();
        this.counters = [];
        this.uid = Math.random().toString(36).slice(2, 8);
        this.openKey = null; // `${step}:${item}` of the single open tray, or null
        this.timers = new Map(); // key -> pending auto-collapse timeout
        this.counts = new Map(); // `${step}:${item}` -> Map(variantId -> units) for pack-builder trays

        this.addEventListener('change', this);
        this.addEventListener('click', this);
        this.addButton.addEventListener('click', () => this.addToCart());
        this.loadProducts().catch(() => this.fail());
      }

      disconnectedCallback() {
        this.removeEventListener('change', this);
        this.removeEventListener('click', this);
      }

      text(key, values = {}) {
        return Object.entries(values).reduce((out, [name, value]) => out.replaceAll(`{${name}}`, value), this.dataset[key] || '');
      }

      fail() {
        this.status.textContent = this.dataset.error;
        this.addButton.hidden = true;
      }

      /** Fetches fresh product data (availability included) for every component into this.products. */
      async fetchProducts() {
        const handles = [...new Set(this.steps.flatMap((step) => step.items.map((item) => item.handle).filter(Boolean)))];
        const root = window.Shopify?.routes?.root || '/';
        const products = await Promise.all(
          handles.map(async (handle) => {
            const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
            if (!response.ok) throw new Error(handle);
            return response.json();
          })
        );
        products.forEach((product) => this.products.set(product.handle, product));
      }

      async loadProducts() {
        await this.fetchProducts();
        this.render();
      }

      render() {
        this.stepsRoot.replaceChildren();
        this.counters = [];
        this.allRequired = []; // per step: the shopper must pick every rendered option
        this.steps.forEach((step, stepIndex) => {
          const fieldset = document.createElement('fieldset');
          fieldset.className = 'bundle-picker__step';

          const legend = document.createElement('legend');
          legend.className = 'bundle-picker__legend';
          const counter = document.createElement('span');
          counter.className = 'bundle-picker__counter';
          legend.append(counter);
          this.counters.push(counter);
          fieldset.append(legend);

          const list = document.createElement('div');
          list.className = 'bundle-picker__options';
          list.dataset.list = stepIndex;
          step.items.forEach((item, itemIndex) => {
            const option = this.renderOption(step, stepIndex, item, itemIndex);
            if (option) list.append(option);
          });
          const allRequired = list.querySelectorAll('.bundle-picker__input').length === step.requiredQuantity;
          this.allRequired.push(allRequired);
          legend.prepend(this.text(allRequired ? 'chooseAll' : 'choose', { count: step.requiredQuantity }));
          fieldset.append(list);
          const trays = document.createElement('div');
          trays.className = 'bundle-picker__trays';
          fieldset.append(trays);
          this.stepsRoot.append(fieldset);
          if (allRequired) this.preselectFixedOptions(stepIndex, list);
        });
        // A step with fewer in-stock options than it requires can never be completed: block the add instead of leaving a dead button.
        this.blocked = this.contract.blockedSteps(this.steps, this.products).length > 0;
        this.update();
      }

      /** In an all-required step, options with a single eligible variant need no choice, so they start picked. */
      preselectFixedOptions(stepIndex, list) {
        list.querySelectorAll('.bundle-picker__option:not([data-tray])').forEach((wrap) => {
          wrap.querySelector('.bundle-picker__input').checked = true;
          this.selections[stepIndex].set(Number(wrap.dataset.item), JSON.parse(wrap.dataset.variants));
        });
      }

      renderOption(step, stepIndex, item, itemIndex) {
        const product = this.products.get(item.handle);
        const variants = this.contract.eligibleVariants(item, product);
        if (!variants.length) return this.renderSoldOut(product); // sold out or outside the allow-list

        const wrap = document.createElement('div');
        wrap.className = 'bundle-picker__option';
        wrap.dataset.step = stepIndex;
        wrap.dataset.item = itemIndex;
        wrap.dataset.variants = JSON.stringify(this.contract.defaultUnitVariantIds(item.qty, variants));
        if (this.usesTray(item, variants)) wrap.dataset.tray = 'true';

        const label = document.createElement('label');
        label.className = 'bundle-picker__choice';

        const input = document.createElement('input');
        input.type = step.requiredQuantity === 1 ? 'radio' : 'checkbox';
        input.className = 'bundle-picker__input';
        input.name = `bundle-${this.id || 'picker'}-${stepIndex}`;
        input.value = String(itemIndex);

        const image = document.createElement('img');
        image.className = 'bundle-picker__image';
        image.loading = 'lazy';
        image.alt = '';
        image.src = (variants[0].featured_image?.src || product.featured_image || '').replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2');

        const name = document.createElement('span');
        name.className = 'bundle-picker__name';
        name.textContent = product.title;

        // The media box only anchors the pending pill; it matches the image's own size so the card layout is unchanged.
        const media = document.createElement('span');
        media.className = 'bundle-picker__media';
        media.append(image);
        if (this.usesTray(item, variants)) {
          const pending = document.createElement('span');
          pending.className = 'bundle-picker__pending';
          pending.setAttribute('aria-hidden', 'true');
          pending.textContent = this.dataset.pendingCard;
          media.append(pending);
        }
        label.append(input, media, name);
        const badge = document.createElement('span');
        badge.className = 'bundle-picker__badge';
        badge.setAttribute('aria-hidden', 'true');
        label.append(badge);
        if (item.qty > 1) {
          const qty = document.createElement('span');
          qty.className = 'bundle-picker__qty';
          const meaning = this.text('qty', { qty: item.qty });
          qty.title = meaning;
          const visible = document.createElement('span');
          visible.setAttribute('aria-hidden', 'true');
          visible.textContent = `×${item.qty}`;
          const spoken = document.createElement('span');
          spoken.className = 'visually-hidden';
          spoken.textContent = meaning;
          qty.append(visible, spoken);
          label.append(qty);
        }
        wrap.append(label);

        return wrap;
      }

      /** Inert card for a component with nothing left to pick: shown so the shopper sees what is missing. */
      renderSoldOut(product) {
        const wrap = document.createElement('div');
        wrap.className = 'bundle-picker__soldout';
        const image = document.createElement('img');
        image.className = 'bundle-picker__image';
        image.loading = 'lazy';
        image.alt = '';
        image.src = (product.featured_image || '').replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2');
        const name = document.createElement('span');
        name.className = 'bundle-picker__name';
        name.textContent = product.title;
        const label = document.createElement('span');
        label.className = 'bundle-picker__soldout-label';
        label.textContent = this.dataset.soldOut;
        wrap.append(image, name, label);
        return wrap;
      }

      usesTray(item, variants) {
        return variants.length > 1; // single-variant items need no control
      }

      eligibleFor(stepIndex, itemIndex) {
        const item = this.steps[stepIndex].items[itemIndex];
        return this.contract.eligibleVariants(item, this.products.get(item.handle));
      }

      /** Creates the tray for newly picked tray items and removes it for unpicked ones. */
      syncTrays(stepIndex) {
        const root = this.stepsRoot.querySelectorAll('.bundle-picker__trays')[stepIndex];
        const selection = this.selections[stepIndex];
        root.querySelectorAll('.bundle-picker__tray').forEach((tray) => {
          if (selection.has(Number(tray.dataset.item))) return;
          const key = `${stepIndex}:${tray.dataset.item}`;
          this.clearTimer(key);
          this.counts.delete(key);
          if (this.openKey === key) this.openKey = null;
          const input = this.optionFor(stepIndex, Number(tray.dataset.item))?.querySelector('.bundle-picker__input');
          input?.removeAttribute('aria-expanded');
          input?.removeAttribute('aria-controls');
          this.optionFor(stepIndex, Number(tray.dataset.item))?.classList.remove('is-done');
          tray.remove();
        });
        selection.forEach((_ids, itemIndex) => {
          const wrap = this.optionFor(stepIndex, itemIndex);
          if (wrap?.dataset.tray !== 'true' || root.querySelector(`.bundle-picker__tray[data-item="${itemIndex}"]`)) return;
          const key = `${stepIndex}:${itemIndex}`;
          this.counts.set(key, new Map());
          const tray = this.renderTray(stepIndex, itemIndex);
          root.append(tray);
          wrap.querySelector('.bundle-picker__input').setAttribute('aria-controls', tray.id);
          this.openKey = key; // opening a tray closes every other one
        });
        this.refreshPanels();
      }

      optionFor(stepIndex, itemIndex) {
        return this.stepsRoot.querySelector(`.bundle-picker__option[data-step="${stepIndex}"][data-item="${itemIndex}"]`);
      }

      trayFor(stepIndex, itemIndex) {
        return this.stepsRoot.querySelector(`.bundle-picker__tray[data-step="${stepIndex}"][data-item="${itemIndex}"]`);
      }

      clearTimer(key) {
        clearTimeout(this.timers.get(key));
        this.timers.delete(key);
      }

      /** Shows only the open tray; keeps the card badge and the card input's aria state in sync with the counts. */
      refreshPanels() {
        this.stepsRoot.querySelectorAll('.bundle-picker__tray').forEach((tray) => {
          const stepIndex = Number(tray.dataset.step);
          const itemIndex = Number(tray.dataset.item);
          const key = `${stepIndex}:${itemIndex}`;
          const item = this.steps[stepIndex].items[itemIndex];
          const total = [...this.counts.get(key).values()].reduce((sum, value) => sum + value, 0);
          const open = this.openKey === key;
          tray.hidden = !open;
          const wrap = this.optionFor(stepIndex, itemIndex);
          wrap.classList.toggle('is-done', total === item.qty);
          wrap.querySelector('.bundle-picker__input').setAttribute('aria-expanded', String(open));
        });
      }

      /** After a pick: collapse a complete tray shortly (focus stays put), or cancel the collapse if it became incomplete. */
      afterPick(stepIndex, itemIndex, tray) {
        const key = `${stepIndex}:${itemIndex}`;
        this.updateTray(stepIndex, itemIndex, tray);
        this.clearTimer(key);
        if (tray.classList.contains('is-complete')) {
          this.timers.set(
            key,
            setTimeout(() => {
              this.timers.delete(key);
              if (this.openKey !== key) return;
              this.openKey = null;
              this.refreshPanels();
            }, 350)
          );
        }
        this.refreshPanels();
        this.update();
      }

      renderTray(stepIndex, itemIndex) {
        const item = this.steps[stepIndex].items[itemIndex];
        const product = this.products.get(item.handle);
        const tray = document.createElement('div');
        tray.className = 'bundle-picker__tray';
        tray.id = `bundle-tray-${this.uid}-${stepIndex}-${itemIndex}`;
        tray.dataset.step = stepIndex;
        tray.dataset.item = itemIndex;

        const head = document.createElement('div');
        head.className = 'bundle-picker__tray-head';
        const title = document.createElement('span');
        title.className = 'bundle-picker__tray-title';
        title.textContent = this.text('units', { title: product.title, count: item.qty });
        const counter = document.createElement('span');
        counter.className = 'bundle-picker__tray-counter';
        counter.setAttribute('role', 'status');
        counter.setAttribute('aria-live', 'polite');
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bundle-picker__tray-remove';
        remove.textContent = this.dataset.removeItem;
        remove.setAttribute('aria-label', `${this.dataset.removeItem}: ${product.title}`);
        head.append(title, counter, remove);

        const bar = document.createElement('div');
        bar.className = 'bundle-picker__tray-bar';
        const fill = document.createElement('div');
        fill.className = 'bundle-picker__tray-fill';
        bar.append(fill);

        const grid = document.createElement('div');
        grid.className = 'bundle-picker__tiles';
        if (item.qty === 1) {
          grid.setAttribute('role', 'radiogroup');
          grid.setAttribute('aria-label', `${this.dataset.variant}: ${product.title}`);
        }
        this.eligibleFor(stepIndex, itemIndex).forEach((variant) => {
          const single = item.qty === 1;
          const tile = document.createElement(single ? 'label' : 'div');
          tile.className = 'bundle-picker__tile';
          tile.dataset.variantId = variant.id;
          if (single) {
            const input = document.createElement('input');
            input.type = 'radio';
            input.className = 'bundle-picker__tile-input';
            input.name = `bundle-${this.id || 'picker'}-${stepIndex}-${itemIndex}-variant`;
            input.value = variant.id;
            tile.append(input);
          }
          if (variant.featured_image?.src) {
            const image = document.createElement('img');
            image.className = 'bundle-picker__tile-image';
            image.loading = 'lazy';
            image.alt = '';
            image.src = variant.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
            tile.append(image);
          }
          const name = document.createElement('span');
          name.className = 'bundle-picker__tile-name';
          name.textContent = variant.title;
          tile.append(name);
          if (!single) {
            const stepper = document.createElement('div');
            stepper.className = 'bundle-picker__stepper';
            const minus = this.renderStepButton('-', -1, `${this.text('remove')} ${variant.title}`.trim());
            const count = document.createElement('span');
            count.className = 'bundle-picker__tile-count';
            const plus = this.renderStepButton('+', 1, `${this.text('addOne')} ${variant.title}`.trim());
            stepper.append(minus, count, plus);
            tile.append(stepper);
          }
          grid.append(tile);
        });

        tray.append(head, bar, grid);
        this.updateTray(stepIndex, itemIndex, tray);
        return tray;
      }

      renderStepButton(symbol, delta, label) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bundle-picker__step-button';
        button.dataset.delta = delta;
        button.setAttribute('aria-label', label);
        button.textContent = symbol;
        return button;
      }

      updateTray(stepIndex, itemIndex, tray) {
        const item = this.steps[stepIndex].items[itemIndex];
        const counts = this.counts.get(`${stepIndex}:${itemIndex}`);
        const total = [...counts.values()].reduce((sum, value) => sum + value, 0);
        tray.classList.toggle('is-complete', total === item.qty);
        tray.querySelector('.bundle-picker__tray-counter').textContent = this.text('selected', { selected: total, count: item.qty });
        tray.querySelector('.bundle-picker__tray-fill').style.width = `${(total / item.qty) * 100}%`;
        tray.querySelectorAll('.bundle-picker__tile').forEach((tile) => {
          const value = counts.get(tile.dataset.variantId) || 0;
          tile.classList.toggle('is-selected', value > 0);
          const input = tile.querySelector('.bundle-picker__tile-input');
          if (input) {
            input.checked = value > 0;
            return;
          }
          tile.querySelector('.bundle-picker__tile-count').textContent = value;
          tile.querySelector('[data-delta="-1"]').disabled = value === 0;
          tile.querySelector('[data-delta="1"]').disabled = total >= item.qty;
        });
      }

      /** Variant ids picked for one option card, one per unit; shorter than item.qty while a tray is incomplete. */
      unitVariantIds(wrap) {
        if (wrap.dataset.tray === 'true') {
          const stepIndex = Number(wrap.dataset.step);
          const itemIndex = Number(wrap.dataset.item);
          return this.contract.expandUnitCounts(this.counts.get(`${stepIndex}:${itemIndex}`), this.eligibleFor(stepIndex, itemIndex));
        }
        return JSON.parse(wrap.dataset.variants);
      }

      /** Single-choice tray (qty 1): the chosen tile replaces any previous choice. */
      handleTilePick(input) {
        const tray = input.closest('.bundle-picker__tray');
        const stepIndex = Number(tray.dataset.step);
        const itemIndex = Number(tray.dataset.item);
        const counts = new Map([[input.value, 1]]);
        this.counts.set(`${stepIndex}:${itemIndex}`, counts);
        this.selections[stepIndex].set(itemIndex, this.contract.expandUnitCounts(counts, this.eligibleFor(stepIndex, itemIndex)));
        this.afterPick(stepIndex, itemIndex, tray);
      }

      handleStepper(button) {
        const tray = button.closest('.bundle-picker__tray');
        const stepIndex = Number(tray.dataset.step);
        const itemIndex = Number(tray.dataset.item);
        const item = this.steps[stepIndex].items[itemIndex];
        const counts = this.counts.get(`${stepIndex}:${itemIndex}`);
        const variantId = button.closest('.bundle-picker__tile').dataset.variantId;
        const total = [...counts.values()].reduce((sum, value) => sum + value, 0);
        const delta = Number(button.dataset.delta);
        const next = (counts.get(variantId) || 0) + delta;
        if (next < 0 || (delta > 0 && total >= item.qty)) return;
        counts.set(variantId, next);
        this.selections[stepIndex].set(
          itemIndex,
          this.contract.expandUnitCounts(counts, this.eligibleFor(stepIndex, itemIndex))
        );
        this.afterPick(stepIndex, itemIndex, tray);
      }

      /** Deselects a tray item from its tray header: clears the pick, drops the tray and returns focus to the card. */
      removeItem(tray) {
        const stepIndex = Number(tray.dataset.step);
        const itemIndex = Number(tray.dataset.item);
        const input = this.optionFor(stepIndex, itemIndex).querySelector('.bundle-picker__input');
        input.checked = false;
        this.selections[stepIndex].delete(itemIndex);
        this.status.textContent = '';
        this.syncTrays(stepIndex);
        this.update();
        input.focus();
      }

      handleEvent(event) {
        if (event.type === 'click') {
          const button = event.target.closest('.bundle-picker__step-button');
          if (button) return this.handleStepper(button);
          const removeButton = event.target.closest('.bundle-picker__tray-remove');
          if (removeButton) return this.removeItem(removeButton.closest('.bundle-picker__tray'));
          // A checked tray card never unchecks by click: it reopens its tray (removal is the tray's own button).
          const card = event.target.closest('.bundle-picker__input');
          const cardWrap = card?.closest('.bundle-picker__option');
          if (cardWrap?.dataset.tray === 'true' && this.selections[Number(cardWrap.dataset.step)].has(Number(cardWrap.dataset.item))) {
            event.preventDefault();
            this.openKey = `${cardWrap.dataset.step}:${cardWrap.dataset.item}`;
            this.refreshPanels();
          }
          return;
        }
        if (event.target.matches('.bundle-picker__tile-input')) return this.handleTilePick(event.target);
        const wrap = event.target.closest('.bundle-picker__option');
        if (!wrap) return;
        const stepIndex = Number(wrap.dataset.step);
        const itemIndex = Number(wrap.dataset.item);
        const selection = this.selections[stepIndex];
        const required = this.steps[stepIndex].requiredQuantity;

        if (!event.target.matches('input')) return;
        if (event.target.checked) {
          if (event.target.type === 'radio') selection.clear();
          if (selection.size >= required) {
            event.target.checked = false;
            this.status.textContent = this.dataset.full;
            return;
          }
          selection.set(itemIndex, this.unitVariantIds(wrap));
        } else {
          selection.delete(itemIndex);
        }
        this.status.textContent = '';
        this.syncTrays(stepIndex);
        this.update();
      }

      update() {
        let complete = true;
        this.steps.forEach((step, stepIndex) => {
          const count = this.selections[stepIndex].size;
          // A picked card counts toward the cap, but only complete picks (exactly qty units) let the shopper add.
          const incomplete = [...this.selections[stepIndex]].some(([itemIndex, ids]) => !this.contract.isItemComplete(step.items[itemIndex], ids));
          if (count !== step.requiredQuantity || incomplete) complete = false;
          // All-required steps count only finished cards ("listo"); other steps count checked cards.
          const ready = this.completePicks(stepIndex).length;
          this.counters[stepIndex].textContent = this.allRequired[stepIndex]
            ? this.text('selectedAll', { selected: ready, count: step.requiredQuantity })
            : this.text('selected', { selected: count, count: step.requiredQuantity });
          const full = count >= step.requiredQuantity;
          this.stepsRoot.querySelectorAll(`.bundle-picker__option[data-step="${stepIndex}"]`).forEach((wrap) => {
            const input = wrap.querySelector('input');
            wrap.classList.toggle('is-selected', input.checked);
            // Trays set their own badge; cards without a tray are complete as soon as they are checked.
            if (wrap.dataset.tray !== 'true') wrap.classList.toggle('is-done', input.checked);
            // Only all-required steps flag unfinished tray cards; N-of-M steps leave the choice open.
            wrap.classList.toggle('is-pending', this.allRequired[stepIndex] && wrap.dataset.tray === 'true' && !wrap.classList.contains('is-done'));
            // Checkboxes lock once the step is full; radios simply swap.
            if (input.type === 'checkbox') input.disabled = full && !input.checked;
          });
        });
        this.addButton.disabled = !complete || this.blocked === true || this.busy === true;
        this.updatePrice();
        const missing = this.contract.countMissing(
          this.steps,
          this.selections.map((_selection, stepIndex) => this.completePicks(stepIndex).map(([itemIndex, variantIds]) => ({ itemIndex, variantIds })))
        );
        if (this.blocked) this.hint.textContent = this.dataset.unavailable;
        else this.hint.textContent = !complete && missing > 0 && this.busy !== true ? this.text('missing', { missing }) : '';
      }

      /** Shows the registry price for the current selection; hidden while the app config gives no price yet. */
      updatePrice() {
        if (!this.priceEl) return;
        const price = this.contract.bundlePrice(this.config, this.selectedLines());
        this.priceEl.hidden = price === null;
        if (price === null) return;
        const rate = Number(window.Shopify?.currency?.rate) || 1;
        const currency = window.Shopify?.currency?.active || 'USD';
        try {
          this.priceEl.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(price * rate);
        } catch {
          this.priceEl.textContent = (price * rate).toFixed(2);
        }
      }

      /** Picks of a step with exactly qty units; incomplete tray picks are left out so validation rejects them. */
      completePicks(stepIndex) {
        return [...this.selections[stepIndex]].filter(([itemIndex, ids]) =>
          this.contract.isItemComplete(this.steps[stepIndex].items[itemIndex], ids)
        );
      }

      selectedLines() {
        return this.selections.flatMap((_selection, stepIndex) =>
          this.completePicks(stepIndex).map(([itemIndex, variantIds]) => ({ item: this.steps[stepIndex].items[itemIndex], variantIds }))
        );
      }

      /** Re-reads availability right before adding; the data loaded with the page can be stale. */
      async refreshAvailability() {
        this.busy = true;
        this.update();
        try {
          await this.fetchProducts();
        } catch {
          // Keep the last known data: Shopify still rejects out-of-stock lines in cart/add.js.
        }
        this.busy = false;
      }

      async addToCart() {
        await this.refreshAvailability();
        const errors = this.contract.validateSelection(
          this.steps,
          this.selections.map((_selection, stepIndex) =>
            this.completePicks(stepIndex).map(([itemIndex, variantIds]) => ({ itemIndex, variantIds }))
          ),
          this.products
        );
        if (errors.length || this.contract.blockedSteps(this.steps, this.products).length) {
          // Stock changed since the page loaded: drop the picks and redraw with the fresh availability.
          this.selections = this.steps.map(() => new Map());
          this.counts.clear();
          this.openKey = null;
          this.render();
          this.status.textContent = this.dataset.stale;
          return;
        }

        const instanceId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const items = this.contract.createCartLines(this.config, this.selectedLines(), instanceId);
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        cart?.setActiveElement?.(document.activeElement);

        this.busy = true;
        this.addButton.setAttribute('aria-busy', 'true');
        this.addButton.textContent = this.dataset.adding;
        this.update();

        try {
          const body = { items };
          if (cart) {
            body.sections = cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
          }
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          // Read as text first: an empty or non-JSON body must show the friendly error, not a raw parse error.
          const text = await response.text();
          let result = {};
          try {
            result = text ? JSON.parse(text) : {};
          } catch {
            console.error('product-bundle-picker: cart/add.js returned a non-JSON body', response.status);
          }
          if (!response.ok || !result.items) {
            console.error('product-bundle-picker: cart/add.js failed', response.status, text.slice(0, 200));
            throw new Error(result.description || result.message || this.dataset.error);
          }

          if (cart?.tagName === 'CART-DRAWER') {
            await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle-picker', cartData: result }).catch(console.error);
            cart.classList.remove('is-empty');
            cart.open(this.addButton);
          } else if (cart) {
            cart.renderContents({ ...result, key: result.items?.[0]?.key });
          } else {
            window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
          }
          this.dispatchEvent(new CustomEvent('cartUpdate', { bubbles: true, detail: 'add' }));
        } catch (error) {
          this.status.textContent = error.message || this.dataset.error;
        } finally {
          this.busy = false;
          this.addButton.removeAttribute('aria-busy');
          this.addButton.textContent = this.dataset.add;
          this.update();
        }
      }
    }
  );
}
